import dayjs, { type Dayjs } from 'dayjs'

import { identity, roundDate, type RoundUnit } from '@dhl/utils'

export type Conversions<A, B> = {
  to: (x: A) => B
  from: (x: B) => A
}

export const defaultConversions: Conversions<Dayjs, Dayjs> = {
  to: identity,
  from: identity,
}

export function composeConversions<A, B, C>(g: Conversions<B, C>, f: Conversions<A, B>): Conversions<A, C> {
  return {
    to: (x: A) => g.to(f.to(x)),
    from: (x: C) => f.from(g.from(x)),
  }
}

export const dateConversions: Conversions<Date | null, Dayjs> = {
  to: (input: Date | null) => dayjs(input),
  from: (date: Dayjs) => (date.isValid() ? date.toDate() : null),
}

/**
 * `strict` defaults to `false` to keep every existing call site behaving exactly as before.
 *
 * When `false`, dayjs's customParseFormat plugin silently fills in any date component it can't
 * cleanly parse (e.g. an incomplete/transient input value) with the corresponding component of the
 * current real-world date, instead of producing an invalid date. That "fallback to today" can make
 * a value being typed silently get pinned to the current month/day/year — pass `strict: true` for
 * a given field if that's a real risk for it (see SBS-1616's overlapping-contracts bug for an
 * example) — but do so per field, since it's a behavior change for whoever consumes it.
 */
export const createConversions = (format: string, strict = false): Conversions<string, Dayjs> => ({
  to: (input: string) => dayjs(input, format, strict),
  from: (date: Dayjs) => date.format(format),
})

export const roundConversions = (amount: number, unit: RoundUnit): Conversions<Dayjs, Dayjs> => ({
  to: (input: Dayjs) => roundDate(input, amount, unit),
  from: (input: Dayjs) => roundDate(input, amount, unit),
})
